import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (
      this.closest('theme-drawer')
    );
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    this.#themeDrawer?.addEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );

    /*
     * Listen for pickup changes.
     * This works even when Horizon replaces
     * the cart drawer contents.
     */
    this.addEventListener(
      'change',
      this.#handlePickupChange
    );

    /*
     * Watch for Horizon re-rendering the drawer.
     */
    this.#pickupObserver = new MutationObserver(() => {
      this.#updatePickupCheckout();
    });

    this.#pickupObserver.observe(this, {
      childList: true,
      subtree: true
    });

    /*
     * The restore path sets [open] before this module loads.
     */
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }

    /*
     * Set the initial checkout state.
     */
    this.#updatePickupCheckout();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    this.#themeDrawer?.removeEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );

    this.removeEventListener(
      'change',
      this.#handlePickupChange
    );

    this.#pickupObserver?.disconnect();
  }

  /**
   * MutationObserver for cart drawer updates.
   * @type {MutationObserver | null}
   */
  #pickupObserver = null;

  /**
   * Handles pickup date/time changes.
   */
  #handlePickupChange = (event) => {
    const target = event.target;

    if (
      !(target instanceof HTMLSelectElement) ||
      !target.matches(
        '.pickup-date-cart, .pickup-time-cart'
      )
    ) {
      return;
    }

    this.#updatePickupCheckout();

    const date = this.querySelector(
      '.pickup-date-cart'
    );

    const time = this.querySelector(
      '.pickup-time-cart'
    );

    if (
      date?.value &&
      time?.value
    ) {
      this.#savePickupInformation(
        date.value,
        time.value
      );
    }
  };

  /**
   * Enable/disable the drawer checkout button.
   */
  #updatePickupCheckout = () => {
    const dialog = this.#dialog;

    if (!dialog) return;

    /*
     * Only target the checkout button inside
     * THIS cart drawer.
     */
    const summary = dialog.querySelector(
      '.cart-drawer__summary'
    );

    if (!summary) return;

    const date = summary.querySelector(
      '.pickup-date-cart'
    );

    const time = summary.querySelector(
      '.pickup-time-cart'
    );

    const checkout = summary.querySelector(
      '.pickup-checkout-button, .cart__checkout-button[name="checkout"]'
    );

    if (
      !date ||
      !time ||
      !checkout
    ) {
      return;
    }

    const ready =
      date.value !== '' &&
      time.value !== '';

    checkout.disabled = !ready;

    /*
     * Make the disabled state visually obvious.
     */
    if (ready) {
      checkout.removeAttribute(
        'aria-disabled'
      );
    } else {
      checkout.setAttribute(
        'aria-disabled',
        'true'
      );
    }
  };

  /**
   * Save pickup information to cart attributes.
   */
  #savePickupInformation = (date, time) => {
    fetch(
      '/cart/update.js',
      {
        method: 'POST',

        headers: {
          'Content-Type':
            'application/json'
        },

        body: JSON.stringify({
          attributes: {
            'Order due date': date,
            'Order due time': time
          }
        })
      }
    ).catch((error) => {
      console.error(
        '[cart-drawer] Failed to save pickup information:',
        error
      );
    });
  };

  /**
   * Handles the theme-drawer opening.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    /*
     * Set pickup checkout state whenever
     * the drawer opens.
     */
    requestAnimationFrame(() => {
      this.#updatePickupCheckout();
    });

    /*
     * Close cart drawer when installments CTA is clicked.
     */
    customElements
      .whenDefined('shopify-payment-terms')
      .then(() => {
        const cta =
          this
            .querySelector(
              'shopify-payment-terms'
            )
            ?.shadowRoot
            ?.querySelector(
              '#shopify-installments-cta'
            );

        cta?.addEventListener(
          'click',
          () => this.#themeDrawer?.close(),
          { once: true }
        );
      });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen =
      this.hasAttribute('auto-open') &&
      event.action === 'add' &&
      !this.#themeDrawer?.isOpen;

    const sourceModal =
      event.target instanceof Element
        ? event.target.closest('dialog:modal')
        : null;

    if (
      shouldAutoOpen &&
      !sourceModal &&
      !this.#isCartEmpty()
    ) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () =>
          requestAnimationFrame(() =>
            this.#updateStickyState()
          );

        if (
          !shouldAutoOpen ||
          detail?.didError
        ) {
          settle();

          requestAnimationFrame(() => {
            this.#updatePickupCheckout();
          });

          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) {
            this.#themeDrawer?.open();
          }

          settle();

          requestAnimationFrame(() => {
            this.#updatePickupCheckout();
          });
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener(
            'close',
            openAndSettle,
            { once: true }
          );
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (
          error?.name !== 'AbortError'
        ) {
          console.warn(
            '[cart-drawer] Event promise rejected:',
            error
          );
        }
      });
  };

  #isCartEmpty() {
    return Boolean(
      this.querySelector(
        '.cart-drawer--empty'
      )
    );
  }

  #updateStickyState() {
    const dialog = this.#dialog;

    if (!dialog) return;

    const content =
      dialog.querySelector(
        '.cart-drawer__content'
      );

    const summary =
      dialog.querySelector(
        '.cart-drawer__summary'
      );

    if (
      !content ||
      !summary
    ) {
      dialog.setAttribute(
        'cart-summary-sticky',
        'false'
      );

      return;
    }

    const drawerHeight =
      dialog.getBoundingClientRect()
        .height;

    const summaryHeight =
      summary.getBoundingClientRect()
        .height;

    const ratio =
      summaryHeight /
      drawerHeight;

    dialog.setAttribute(
      'cart-summary-sticky',
      ratio >
        this.#summaryThreshold
        ? 'false'
        : 'true'
    );
  }
}

if (
  !customElements.get(
    'cart-drawer-component'
  )
) {
  customElements.define(
    'cart-drawer-component',
    CartDrawerComponent
  );
}